import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';
import { hasRole } from '../lib/utils';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');

    if (!token) {
      setLoading(false);
      return;
    }

    // Hidratar el usuario desde caché de inmediato para mantener la sesión en el
    // reload (pull-to-refresh en móvil), sin esperar a la validación del server.
    if (savedUser) {
      try { setUser(JSON.parse(savedUser)); } catch (e) { /* ignore */ }
    }

    try {
      const response = await authApi.getMe();
      const userData = response.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (err) {
      const status = err.response?.status;
      // Solo cerrar sesión ante un error de auth REAL (401/403). Errores de red o
      // timeout (frecuentes durante el reload por pull-to-refresh) NO deben desloguear:
      // se conserva la sesión cacheada.
      if (status === 401 || status === 403) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        setUser(null);
      } else {
        console.warn('Auth check no concluyente (se mantiene la sesión):', err?.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authApi.login({ email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al iniciar sesión';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (data) => {
    setError(null);
    try {
      const response = await authApi.register(data);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al registrar';
      setError(message);
      throw new Error(message);
    }
  };

  const processGoogleSession = async (sessionId) => {
    setError(null);
    try {
      const response = await authApi.processSession(sessionId);
      const userData = response.data;
      
      // For Google auth, the session is stored in cookie
      // We also store user data locally
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al procesar sesión de Google';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    processGoogleSession,
    checkAuth,
    isAuthenticated: !!user,
    isPropietario: hasRole(user, 'propietario'),
    isAnalista: hasRole(user, 'analista'),
    isAdmin: hasRole(user, 'admin'),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
